/**
 * The header tiles' clock pieces (19/10/2026): a country minute on the
 * Danish clock with a small "−1d" tag when that is Denmark's evening before
 * (Australia's morning), and a clock line led by its flag — "🇩🇰 15:00 → 07:00"
 * over "🇦🇺 00:00 → 16:00" — so both clocks line up. Without a timezone the
 * country's time shows as it is.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';

import { dkTime, shiftMark } from '../../../utils/clock/dk-label';
import { minutesToHHMM } from '../../../utils/format';

const DayTag: FC<{ shift: number }> = ({ shift }) => (
  <span
    className="ml-1 rounded bg-muted px-1 py-0.5 align-middle text-[10px] font-medium leading-none text-muted-foreground"
    title={shift < 0 ? 'The evening before, in Denmark' : 'The next day, in Denmark'}
  >
    {shiftMark(shift).trim()}
  </span>
);

export const DkTime: FC<{ min: number; tz: string | null; runDate?: string | null }> = ({ min, tz, runDate }) => {
  const t = dkTime(min, tz, runDate);
  if (!t) return <>{minutesToHHMM(min)}</>;
  return (
    <>
      {t.label}
      {t.shift !== 0 ? <DayTag shift={t.shift} /> : null}
    </>
  );
};

/** A clock's line, its flag first. */
export const ClockLine: FC<{ flag: string | null; children: ReactNode }> = ({ flag, children }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap">
    {flag ? (
      <span aria-hidden className="text-[0.9em] leading-none">
        {flag}
      </span>
    ) : null}
    <span className="min-w-0 truncate">{children}</span>
  </span>
);
